import { useEffect, useMemo, useState } from 'react';

/**
 * Paginação client-side padrão do sistema.
 *
 * Recebe a lista já filtrada e devolve a fatia da página atual junto com tudo
 * que o <Pagination /> precisa. Só existe um lugar decidindo o que é "página",
 * então as tabelas não divergem mais entre si.
 *
 * @param {Array} items lista completa (já filtrada/ordenada)
 * @param {number} initialPageSize quantidade inicial por página
 */
export default function usePagination(items, initialPageSize = 10) {
  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(initialPageSize);

  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Filtro encolheu a lista e a página atual deixou de existir: puxa pra última
  // que ainda tem conteúdo, em vez de mostrar tabela vazia.
  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return list.slice(start, start + pageSize);
  }, [list, page, pageSize]);

  const setPageSize = (size) => {
    setPageSizeState(Number(size));
    setPage(1);
  };

  return {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    total,
    pageItems,
    from: total === 0 ? 0 : (page - 1) * pageSize + 1,
    to: Math.min(page * pageSize, total),
    /** volta pro início — use quando um filtro muda */
    resetPage: () => setPage(1),
  };
}
